import {
  assignSolarPanelsToString,
  removeSolarInverterInstance,
  type SolarElectricalTopology,
  solarElectricalTopologySchema,
} from './solarElectricalTopology';
import {
  solarDesignDraftStateSchema,
  type SolarDesignDraftState,
} from './solarDesignDraft';

export type SolarElectricalChange =
  | {
      type: 'addInverter';
      id: string;
      selectionId: string;
      applianceId: string;
      name: string;
    }
  | { type: 'renameInverter'; id: string; name: string }
  | {
      type: 'reviseInverter';
      id: string;
      selectionId: string;
      applianceId: string;
    }
  | {
      type: 'revisePanelGroup';
      groupId: string;
      selectionId: string;
      applianceId: string;
    }
  | { type: 'removeInverter'; id: string }
  | { type: 'addString'; id: string; name: string }
  | { type: 'renameString'; id: string; name: string }
  | { type: 'removeString'; id: string }
  | { type: 'assignPanels'; stringId: string | null; panelIds: string[] }
  | {
      type: 'declareInput';
      inverterInstanceId: string;
      mpptKey: string;
      inputKey: string;
    }
  | {
      type: 'connectString';
      id: string;
      inverterInstanceId: string | null;
      mpptKey: string | null;
      inputKey: string | null;
    };

/** Both the UI and structured API apply exactly one identity-based edit. */
export const applySolarElectricalChange = (
  source: SolarElectricalTopology,
  change: SolarElectricalChange
): { topology: SolarElectricalTopology; error: string | null } => {
  const topology = structuredClone(source);
  topology.validation = null;
  const inverter =
    'id' in change
      ? topology.inverterInstances.find((item) => item.id === change.id)
      : undefined;
  const string =
    'id' in change
      ? topology.strings.find((item) => item.id === change.id)
      : undefined;
  switch (change.type) {
    case 'revisePanelGroup':
    case 'reviseInverter': {
      const group =
        change.type === 'revisePanelGroup'
          ? topology.placementGroups.find((item) => item.id === change.groupId)
          : undefined;
      const target = change.type === 'reviseInverter' ? inverter : group;
      if (!target)
        return {
          topology: source,
          error: 'Choose an existing inverter instance or placement group.',
        };
      if (
        topology.equipmentSelections.some(
          (item) => item.id === change.selectionId
        )
      )
        return {
          topology: source,
          error:
            'A specification revision requires a new equipment selection identity.',
        };
      topology.equipmentSelections.push({
        id: change.selectionId,
        applianceId: change.applianceId,
        productSpecId: null,
        contentHash: null,
        specSnapshot: null,
      });
      target.equipmentSelectionId = change.selectionId;
      if (inverter) {
        inverter.trackers = [];
        topology.strings = topology.strings.map((item) =>
          item.inverterInstanceId === inverter.id
            ? {
                ...item,
                inverterInstanceId: null,
                mpptKey: null,
                inputKey: null,
              }
            : item
        );
      }
      break;
    }
    case 'addInverter':
      if (
        topology.inverterInstances.some((item) => item.id === change.id) ||
        topology.equipmentSelections.some(
          (item) => item.id === change.selectionId
        )
      )
        return {
          topology: source,
          error: 'Use new identities for the inverter and equipment selection.',
        };
      topology.equipmentSelections.push({
        id: change.selectionId,
        applianceId: change.applianceId,
        productSpecId: null,
        contentHash: null,
        specSnapshot: null,
      });
      topology.inverterInstances.push({
        id: change.id,
        name: change.name,
        equipmentSelectionId: change.selectionId,
        quantity: 1,
        trackers: [],
      });
      break;
    case 'renameInverter':
      if (!inverter)
        return {
          topology: source,
          error: 'Choose an existing inverter instance.',
        };
      inverter.name = change.name;
      break;
    case 'removeInverter':
      if (!inverter)
        return {
          topology: source,
          error: 'Choose an existing inverter instance.',
        };
      return {
        topology: removeSolarInverterInstance(topology, change.id),
        error: null,
      };
    case 'addString':
      if (topology.strings.some((item) => item.id === change.id))
        return {
          topology: source,
          error: 'Use a new identity for the string.',
        };
      topology.strings.push({
        id: change.id,
        name: change.name,
        panelIds: [],
        inverterInstanceId: null,
        mpptKey: null,
        inputKey: null,
      });
      break;
    case 'renameString':
      if (!string)
        return { topology: source, error: 'Choose an existing string.' };
      string.name = change.name;
      break;
    case 'removeString':
      if (!string)
        return { topology: source, error: 'Choose an existing string.' };
      topology.strings = topology.strings.filter(
        (item) => item.id !== change.id
      );
      break;
    case 'assignPanels': {
      if (change.stringId != null) {
        if (!topology.strings.some((item) => item.id === change.stringId))
          return { topology: source, error: 'Choose an existing string.' };
        return {
          topology: assignSolarPanelsToString(
            topology,
            change.stringId,
            change.panelIds
          ),
          error: null,
        };
      }
      const ids = new Set(change.panelIds);
      topology.strings = topology.strings.map((item) => ({
        ...item,
        panelIds: item.panelIds.filter((id) => !ids.has(id)),
      }));
      break;
    }
    case 'declareInput': {
      const target = topology.inverterInstances.find(
        (item) => item.id === change.inverterInstanceId
      );
      if (!target)
        return {
          topology: source,
          error: 'Choose an existing inverter instance.',
        };
      const tracker = target.trackers.find(
        (item) => item.key === change.mpptKey
      );
      if (
        target.trackers.some((item) =>
          item.inputs.some((input) => input.key === change.inputKey)
        )
      )
        return {
          topology: source,
          error: 'That physical input is already declared on this inverter.',
        };
      if (tracker) tracker.inputs.push({ key: change.inputKey });
      else
        target.trackers.push({
          key: change.mpptKey,
          provenance: 'user_declared',
          inputs: [{ key: change.inputKey }],
        });
      break;
    }
    case 'connectString':
      if (!string)
        return { topology: source, error: 'Choose an existing string.' };
      if (change.inverterInstanceId == null) {
        if (change.mpptKey != null || change.inputKey != null)
          return {
            topology: source,
            error: 'Disconnect the MPPT and physical input with the inverter.',
          };
      } else {
        const target = topology.inverterInstances.find(
          (item) => item.id === change.inverterInstanceId
        );
        const tracker = target?.trackers.find(
          (item) => item.key === change.mpptKey
        );
        if (!tracker?.inputs.some((item) => item.key === change.inputKey))
          return {
            topology: source,
            error: 'Choose a declared inverter, MPPT and physical input.',
          };
        if (
          topology.strings.some(
            (item) =>
              item.id !== change.id &&
              item.inverterInstanceId === change.inverterInstanceId &&
              item.inputKey === change.inputKey
          )
        )
          return {
            topology: source,
            error: 'That physical input already has an assigned string.',
          };
      }
      Object.assign(string, {
        inverterInstanceId: change.inverterInstanceId,
        mpptKey: change.mpptKey,
        inputKey: change.inputKey,
      });
      break;
  }
  const parsed = solarElectricalTopologySchema.safeParse(topology);
  return parsed.success
    ? { topology: parsed.data, error: null }
    : {
        topology: source,
        error: parsed.error.issues[0]?.message ?? 'Check the allocation edit.',
      };
};

/** Keep panel references and group revisions atomic in both clients. */
export const applySolarElectricalDesignChange = (
  state: SolarDesignDraftState,
  change: SolarElectricalChange
): { state: SolarDesignDraftState; error: string | null } => {
  if (state.electrical.mode !== 'allocated')
    return {
      state,
      error: 'Enable allocation before editing electrical equipment.',
    };
  const result = applySolarElectricalChange(state.electrical, change);
  if (result.error) return { state, error: result.error };
  const updated = {
    ...state,
    electrical: result.topology,
    layout:
      change.type === 'revisePanelGroup'
        ? {
            ...state.layout,
            workingPanels: state.layout.workingPanels.map((panel) =>
              panel.placementGroupId === change.groupId
                ? { ...panel, equipmentSelectionId: change.selectionId }
                : panel
            ),
          }
        : state.layout,
  };
  const parsed = solarDesignDraftStateSchema.safeParse(updated);
  return parsed.success
    ? { state: parsed.data, error: null }
    : {
        state,
        error: parsed.error.issues[0]?.message ?? 'Check the allocation edit.',
      };
};
